/**
 * Shared verse card face: classical portrait + square art.
 * Drop final art at /art/cards/{verseId}.webp — missing files keep the placeholder.
 *
 * Layout: 1:1 art with overlaid nameplate (book + large chapter:verse, cost pip)
 * → optional foot (meta/effects). Hand peeks the top of the art.
 */

import { bindArtImage, isArtKnownMissing } from "./artCache";

export function cardArtSrc(verseId: string): string {
  return `/art/cards/${verseId}.webp`;
}

export interface CardArtOptions {
  verseId: string;
  category: string;
  /** Full verse reference — drives monogram + nameplate */
  reference?: string;
  /** Proclaim lock duration shown as the cost pip. */
  speakSeconds?: number;
  /**
   * Abbreviate long book names on the nameplate (hand / grid).
   * Default true. Study / reveal pass false for the full book.
   */
  compactRef?: boolean;
}

/** Build the 1:1 art frame with an overlaid verse nameplate. */
export function createCardArt(opts: CardArtOptions): HTMLElement {
  const art = document.createElement("div");
  art.className = `card-art category-${opts.category}`;
  art.dataset.verseId = opts.verseId;

  const placeholder = document.createElement("div");
  placeholder.className = "card-art-placeholder";
  placeholder.setAttribute("aria-hidden", "true");

  const monogram = document.createElement("span");
  monogram.className = "card-art-monogram";
  monogram.textContent = monogramFromId(opts.verseId, opts.reference);
  placeholder.appendChild(monogram);

  art.appendChild(placeholder);

  const src = cardArtSrc(opts.verseId);
  if (isArtKnownMissing(src)) {
    art.classList.add("no-art");
  } else {
    const img = document.createElement("img");
    img.className = "card-art-img";
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";
    img.draggable = false;
    bindArtImage(img, src, () => {
      img.remove();
      art.classList.add("no-art");
    });
    art.appendChild(img);
  }

  if (opts.reference) {
    art.appendChild(
      createCardNameplate({
        reference: opts.reference,
        speakSeconds: opts.speakSeconds,
        compact: opts.compactRef !== false,
      }),
    );
  }
  return art;
}

export interface CardNameplateOptions {
  reference: string;
  speakSeconds?: number;
  /** Abbreviate long book names. Default true. */
  compact?: boolean;
}

/**
 * Overlay identity: book label + large chapter:verse, optional cost pip.
 * Sits on the art (hand peeks the top).
 */
export function createCardNameplate(opts: CardNameplateOptions): HTMLElement {
  const plate = document.createElement("div");
  plate.className = "card-nameplate";

  const parsed = parseReference(opts.reference);
  const stack = document.createElement("div");
  stack.className = "card-nameplate-ref";

  const fullName = displayBook(parsed.book, opts.compact !== false);
  const shortName = shortBook(parsed.book);
  const bookFull = document.createElement("span");
  bookFull.className = "card-nameplate-book is-full";
  bookFull.textContent = fullName;
  stack.appendChild(bookFull);
  const bookShort = document.createElement("span");
  bookShort.className = "card-nameplate-book is-short";
  bookShort.textContent = shortName;
  stack.appendChild(bookShort);

  if (parsed.locator) {
    const loc = document.createElement("span");
    loc.className = "card-nameplate-locator";
    loc.textContent = parsed.locator;
    stack.appendChild(loc);
  }
  plate.appendChild(stack);
  plate.title = opts.reference;

  if (opts.speakSeconds !== undefined) {
    const cost = document.createElement("span");
    cost.className = "cost";
    cost.textContent = String(opts.speakSeconds);
    cost.title = "Proclaim time (seconds)";
    plate.appendChild(cost);
  }

  return plate;
}

function monogramFromId(id: string, reference?: string): string {
  if (reference) {
    const m = reference.match(/^([1-3A-Za-z]+)/);
    if (m) return m[1]!.slice(0, 3).toUpperCase();
  }
  return id.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase() || "✦";
}

/** Common book abbreviations for tight hand / grid chrome. */
const BOOK_ABBR: Record<string, string> = {
  genesis: "Gen",
  exodus: "Exod",
  leviticus: "Lev",
  numbers: "Num",
  deuteronomy: "Deut",
  joshua: "Josh",
  judges: "Judg",
  ruth: "Ruth",
  samuel: "Sam",
  kings: "Kgs",
  chronicles: "Chr",
  ezra: "Ezra",
  nehemiah: "Neh",
  esther: "Esth",
  job: "Job",
  psalm: "Ps",
  psalms: "Ps",
  proverbs: "Prov",
  ecclesiastes: "Eccl",
  isaiah: "Isa",
  jeremiah: "Jer",
  lamentations: "Lam",
  ezekiel: "Ezek",
  daniel: "Dan",
  hosea: "Hos",
  joel: "Joel",
  amos: "Amos",
  obadiah: "Obad",
  jonah: "Jonah",
  micah: "Mic",
  nahum: "Nah",
  habakkuk: "Hab",
  zephaniah: "Zeph",
  haggai: "Hag",
  zechariah: "Zech",
  malachi: "Mal",
  matthew: "Matt",
  mark: "Mk",
  luke: "Lk",
  john: "Jn",
  acts: "Acts",
  romans: "Rom",
  corinthians: "Cor",
  galatians: "Gal",
  ephesians: "Eph",
  philippians: "Phil",
  colossians: "Col",
  thessalonians: "Thess",
  timothy: "Tim",
  titus: "Titus",
  philemon: "Phlm",
  hebrews: "Heb",
  james: "Jas",
  peter: "Pet",
  jude: "Jude",
  revelation: "Rev",
};

export interface ParsedReference {
  /** "Philippians" / "1 Thessalonians" */
  book: string;
  /** "4:6–7" (en dash) — empty if the string did not parse */
  locator: string;
  full: string;
}

const REF_RE =
  /^((?:[1-3]\s+)?[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+:\d+(?:\s*[-–—]\s*\d+)?)\s*$/;

export function parseReference(reference: string): ParsedReference {
  const m = reference.trim().match(REF_RE);
  if (!m) return { book: reference.trim(), locator: "", full: reference };
  return {
    book: m[1]!.replace(/\s+/g, " "),
    locator: m[2]!.replace(/\s*[-–—]\s*/, "–"),
    full: reference,
  };
}

/** Book line for the nameplate. Compact shortens only the longest names. */
export function displayBook(book: string, compact: boolean): string {
  const cleaned = book.replace(/\s+/g, " ").trim();
  // 2 Corinthians (13) still fits; 1 Thessalonians (15) does not.
  if (!compact || cleaned.length <= 14) return cleaned.toUpperCase();
  return shortBook(cleaned);
}

/** Short book for tight faces. Keeps names that already fit (Psalm, Mark). */
export function shortBook(book: string): string {
  const cleaned = book.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 8) return cleaned.toUpperCase();
  const m = cleaned.match(/^([1-3]\s+)?(.+)$/);
  const num = m?.[1] ?? "";
  const name = m?.[2] ?? cleaned;
  const abbr = BOOK_ABBR[name.toLowerCase()];
  if (abbr) return `${num}${abbr}`.replace(/\s+/g, " ").trim().toUpperCase();
  const short = name.length <= 5 ? name : name.slice(0, 4);
  return `${num}${short}`.replace(/\s+/g, " ").trim().toUpperCase();
}

/** Compact verse reference for small chrome (e.g. "Phil 4:6-7"). */
export function shortenRef(reference: string): string {
  return reference.replace(
    /^([1-3]?\s*)([A-Za-z]+)\b/,
    (_, num: string, book: string) => {
      const abbr = BOOK_ABBR[book.toLowerCase()];
      if (abbr) return `${num}${abbr}`;
      return `${num}${book.length <= 5 ? book : book.slice(0, 4)}`;
    },
  );
}

export interface VerseCardFaceOptions {
  verseId: string;
  reference: string;
  category: string;
  /** Proclaim lock duration (seconds) shown as cost. */
  speakSeconds?: number;
  /** Category · role / kind line */
  metaLine?: string;
  /** Effect summary or blurb (always visible on display cards) */
  effectLine?: string;
  /** Optional keyword labels for the foot */
  keywordLabels?: string[];
  /** deck | reveal | fullscreen | static (no hand peek positioning) */
  size?: "deck" | "reveal" | "fullscreen" | "static";
  selected?: boolean;
}

/**
 * Full static card face for deck builder / reveal enlarge (not hand interaction).
 */
export function createVerseCardFace(opts: VerseCardFaceOptions): HTMLElement {
  const size = opts.size ?? "static";
  const card = document.createElement("article");
  card.className = `card category-${opts.category} card-display card-size-${size}`;
  if (opts.selected) card.classList.add("selected");
  card.dataset.verseId = opts.verseId;

  const compactRef = size === "deck" || size === "static";
  const art = createCardArt({
    verseId: opts.verseId,
    category: opts.category,
    reference: opts.reference,
    speakSeconds: opts.speakSeconds,
    compactRef,
  });

  const foot = document.createElement("div");
  foot.className = "card-foot";

  if (opts.metaLine) {
    const cat = document.createElement("div");
    cat.className = "cat";
    cat.textContent = opts.metaLine;
    foot.appendChild(cat);
  }

  if (opts.keywordLabels?.length) {
    const row = document.createElement("div");
    row.className = "keyword-row card-face-keywords";
    for (const label of opts.keywordLabels.slice(0, 4)) {
      const chip = document.createElement("span");
      chip.className = "keyword-chip";
      chip.textContent = label;
      row.appendChild(chip);
    }
    foot.appendChild(row);
  } else if (opts.effectLine) {
    const peek = document.createElement("div");
    peek.className = "effect-peek";
    peek.textContent = opts.effectLine;
    foot.appendChild(peek);
  }

  // No foot → art fills the 5:7 frame (nameplate lives on the art)
  if (foot.childElementCount > 0) {
    card.append(art, foot);
  } else {
    card.classList.add("clean-face");
    card.append(art);
  }
  return card;
}
